import {
  getQueryKey,
  useGqlMutation,
  useOrgId,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import {
  vehiclePurchasableExtrasQuery,
  vehicleRecordQuery,
} from "@/app/Vehicles/GraphQL/vehicleRecordQuery";
import { purchasableExtrasQuery } from "@/app/Suppliers/GraphQL/supplierPurchasableExtrasQuery";
import { applyVehiclePurchasableExtrasMutation } from "@/app/Vehicles/GraphQL/vehicleMutations";
import { Switch, SwitchField } from "@/components/catalyst/switch";
import { Label } from "@/components/catalyst/fieldset";
import { FeatureFlagType, RelocationType } from "@/gql/graphql";
import { useFeatureFlag } from "@/app/Meta/utils/useFeatureFlag";
import { useQueryClient } from "@tanstack/react-query";
import { formatPurchasableExtraPrice } from "@/app/Suppliers/Utils/formatPurchasableExtraPrice";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { SupplierPurchasableExtraCreateDialog } from "@/app/Suppliers/Components/SupplierPurchasableExtraCreateDialog";
import { SupplierPurchasableExtraUpdateDialog } from "@/app/Suppliers/Components/SupplierPurchasableExtraUpdateDialog";
import { IconButton } from "@/lib/Components/Button/IconButton";
import { PencilSquareIcon, PlusIcon } from "@heroicons/react/24/outline";

export function VehiclePurchasableExtraList({
  vehicleId,
}: {
  vehicleId: string;
}) {
  const queryClient = useQueryClient();
  const orgId = useOrgId();
  const { mutateAsync } = useGqlMutation(
    applyVehiclePurchasableExtrasMutation,
    {
      onSuccess: () => {
        const queryKey = getQueryKey(
          vehiclePurchasableExtrasQuery,
          { vehicleId },
          orgId,
        );

        return queryClient.invalidateQueries({ queryKey });
      },
    },
  );

  const { data: vehicleRes } = useSuspenseGqlQuery(vehicleRecordQuery, {
    id: vehicleId,
  });

  const { data: vehicleExtras } = useSuspenseGqlQuery(
    vehiclePurchasableExtrasQuery,
    { vehicleId },
  );

  const supplierId = vehicleRes.vehicle.supplier.id;

  const { data } = useSuspenseGqlQuery(purchasableExtrasQuery, {
    first: 200,
    page: 1,
    supplierId,
  });

  const { open: openCreate } = useDialog(SupplierPurchasableExtraCreateDialog);
  const { open: openEdit } = useDialog(SupplierPurchasableExtraUpdateDialog);

  const extras = data.supplierPurchasableExtras.data;
  const gapRentalsEnabled = useFeatureFlag(FeatureFlagType.GapRentals);

  const toggles = [
    { relocationType: RelocationType.Relocation, label: "Relocations" },
    ...(gapRentalsEnabled
      ? [{ relocationType: RelocationType.GapRental, label: "Gap rentals" }]
      : []),
  ];

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {extras.map((extra) => {
        return (
          <div
            key={extra.id}
            className="relative rounded-lg border border-gray-200 bg-white p-5 shadow-sm transition-all hover:border-gray-300 hover:shadow-md"
          >
            <div className="absolute right-3 top-3">
              <IconButton
                Icon={PencilSquareIcon}
                tooltip="Edit extra"
                onClick={() =>
                  openEdit({
                    supplierPurchasableExtra: extra,
                    currency: extra.supplier.currency,
                  })
                }
              />
            </div>

            <div className="mb-3 flex items-start gap-3 pr-8">
              {extra.image ? (
                <img
                  src={extra.image.url}
                  alt={extra.name}
                  className="size-14 shrink-0 rounded-md border border-gray-200 object-cover"
                />
              ) : null}
              <div>
                <div className="text-base font-medium text-gray-900">
                  {extra.name}
                </div>
                <div className="mt-1 text-sm font-medium text-gray-600">
                  {formatPurchasableExtraPrice(
                    extra.unit_price,
                    extra.supplier.currency,
                  )}
                </div>
              </div>
            </div>
            <p className="mb-4 text-sm leading-relaxed text-gray-500">
              {extra.description}
            </p>

            <div className="flex flex-col gap-3 border-t border-gray-100 pt-4">
              {toggles.map(({ relocationType, label }) => {
                const enabled =
                  vehicleExtras.vehicle.vehiclePurchasableExtras.some(
                    (e) =>
                      e.supplierPurchasableExtra.id === extra.id &&
                      e.relocation_type === relocationType,
                  );

                return (
                  <SwitchField key={relocationType}>
                    <Label className="text-sm font-normal text-gray-700">
                      {label}
                    </Label>
                    <Switch
                      color="blue"
                      checked={enabled}
                      name={`${extra.name}-${relocationType}`}
                      onChange={async (changed) => {
                        await mutateAsync({
                          vehicleId,
                          supplierPurchasableExtraId: extra.id,
                          enabled: changed,
                          relocationType,
                        });
                      }}
                    />
                  </SwitchField>
                );
              })}
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={() => openCreate({ supplierId })}
        className="flex min-h-[132px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-200 p-4 text-sm font-semibold text-gray-500 transition hover:border-indigo-300 hover:bg-indigo-50/40 hover:text-indigo-600 focus:outline-none focus-visible:border-indigo-400"
      >
        <PlusIcon className="size-6" />
        Add extra
      </button>
    </div>
  );
}
